import { API_URL } from "../../../../constants/urls.constants";
import type { GetQueryArgsType } from "../../../../types/global.types";
import { appServiceApi } from "../../../apis/app-service.api";
import { APP_API_TAGS } from "../../../app.constants";
import { invalidateOnSuccess } from "../../../app.utils";
import type {
  AllApplicantsParams,
  AllApplicantsResponse,
  AllAssignedAssessmentsResponse,
  AllJobPostsResponse,
  AllTalentHuntJobPostsResponse,
  ApplicationTalentHuntJobParams,
  ApplicationTalentHuntJobResponse,
  AssignedAssessmentParams,
  JobPostParams,
  ShortlistedApplicantsResponse,
  SingleApplicantResponse,
  SingleJobPostResponse,
  SyncCheckAiSearchTalentHuntJobResponse,
  TalentHuntJobPostParams,
} from "./job-applicants.type";

const {
  JOB_POSTS,
  JOB_APPLICANTS,
  SHORTLIST,
  ASSESSMENTS,
  TALENT_HUNT_JOB_POSTS,
  APPLICANT_TALENT_HUNT_JOB,
  SYNC_TALENT_HUNT_JOB,
  AI_GENERATED_INTERVIEW_QUESTIONS,
} = APP_API_TAGS || {};

export const jobApplicantsApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET: All Job Posts
    getAllJobPosts: builder.query<
      AllJobPostsResponse,
      GetQueryArgsType<JobPostParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.recruitmentJobs}`,
        method: "GET",
        ...queryArgs,
      }),
      providesTags: [JOB_POSTS],
    }),

    // GET: Single Job Post
    getSingleJobPost: builder.query<SingleJobPostResponse, string>({
      query: (id) => ({
        url: `${API_URL.recruitmentJobs}/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: JOB_POSTS as any, id }],
    }),

    // POST: Create Job Post
    createJobPost: builder.mutation({
      query: (payload) => ({
        url: `${API_URL.recruitmentJobs}`,
        method: "POST",
        body: payload?.body,
      }),
      invalidatesTags: invalidateOnSuccess(JOB_POSTS),
    }),

    // PATCH: Update Job Post
    updateJobPost: builder.mutation({
      query: (payload) => ({
        url: `${API_URL.recruitmentJobs}/${payload?.id}`,
        method: "PATCH",
        body: payload?.body,
      }),
      invalidatesTags: invalidateOnSuccess([
        JOB_POSTS,
        { type: JOB_POSTS, id: true },
      ]),
    }),

    // DELETE: Delete Job Post
    deleteJobPost: builder.mutation({
      query: (payload) => ({
        url: `${API_URL.recruitmentJobs}/${payload?.id}`,
        method: "DELETE",
      }),
      invalidatesTags: invalidateOnSuccess([
        JOB_POSTS,
        { type: JOB_POSTS, id: true },
      ]),
    }),

    // POST: Sync Job Status (Bulk Update)
    syncJobStatus: builder.mutation({
      query: (payload) => ({
        url: `${API_URL.syncJobStatus}`,
        method: "POST",
        body: payload?.body,
      }),
      invalidatesTags: invalidateOnSuccess(JOB_POSTS),
    }),

    // ------ Applicants Endpoints ------

    // GET: All Applicants
    getAllApplicants: builder.query<
      AllApplicantsResponse,
      GetQueryArgsType<AllApplicantsParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.recruitmentApplicants}`,
        method: "GET",
        ...queryArgs,
      }),
      providesTags: [JOB_APPLICANTS],
    }),

    // GET: Single Applicant
    getSingleApplicants: builder.query<SingleApplicantResponse, string>({
      query: (id) => ({
        url: `${API_URL.recruitmentApplicants}/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [
        { type: JOB_APPLICANTS as any, id },
      ],
    }),

    // DELETE: Delete Applicant
    deleteApplicants: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `${API_URL.recruitmentApplicants}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: invalidateOnSuccess([
        JOB_APPLICANTS,
        { type: JOB_APPLICANTS, id: true },
      ]),
    }),

    // POST: Shortlist CV Processing
    shortlistCV: builder.mutation<any, any>({
      query: (body) => ({
        url: `${API_URL.recruitmentCVProcessing}`,
        method: "POST",
        body,
      }),
      invalidatesTags: invalidateOnSuccess([JOB_APPLICANTS, SHORTLIST]),
    }),

    // POST: Manual Shortlist
    shortlistApplicantsManual: builder.mutation<
      any,
      { job_application_ids: string[] }
    >({
      query: (body) => ({
        url: API_URL.recruitmentManualShortlist,
        method: "POST",
        body,
      }),
      invalidatesTags: invalidateOnSuccess([JOB_APPLICANTS, SHORTLIST]),
    }),

    // GET: All Shortlisted Applicants
    getShortListedApplicants: builder.query<
      ShortlistedApplicantsResponse,
      GetQueryArgsType<{
        page?: number;
        limit?: number;
        search?: string;
        interview_status?: string;
      }>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.recruitmentShortlist}`,
        method: "GET",
        ...queryArgs,
      }),
      providesTags: [SHORTLIST],
    }),

    // POST: Analyze Applicant CV with AI
    analyzeApplicantCv: builder.mutation<any, any>({
      query: (body) => ({
        url: `${API_URL.recruitmentAnalyzeCvAi}`,
        method: "POST",
        body,
      }),
      invalidatesTags: invalidateOnSuccess([JOB_APPLICANTS, SHORTLIST]),
    }),

    // GET: Applicant CV Analysis Details
    getApplicantAnalysis: builder.query<any, string>({
      query: (cvId) => ({
        url: `${API_URL.recruitmentCvAnalysisDetails}/${cvId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, cvId) => [
        { type: SHORTLIST as any, id: cvId },
      ],
    }),
    // POST: Assign Assessment
    assignAssessment: builder.mutation<any, any>({
      query: (body) => ({
        url: `${API_URL.recruitmentAssessmentAssign}`,
        method: "POST",
        body,
      }),
      invalidatesTags: invalidateOnSuccess([
        JOB_APPLICANTS,
        ASSESSMENTS,
        SHORTLIST,
      ]),
    }),

    // GET: All Assigned Assessments
    getAllAssignedAssessment: builder.query<
      AllAssignedAssessmentsResponse,
      GetQueryArgsType<AssignedAssessmentParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.recruitmentAssessmentAssign}`,
        method: "GET",
        ...queryArgs,
      }),
      providesTags: [ASSESSMENTS],
    }),

    // GET: AI Generated Interview Questions
    getAIGeneratedInterviewQuestions: builder.query<any, string>({
      query: (id) => ({
        url: `${API_URL.recruitmentJobs}/${id}/interview-questions`,
        method: "GET",
      }),
      providesTags: [AI_GENERATED_INTERVIEW_QUESTIONS],
    }),

    // POST: Generate AI Interview Questions
    generateInterviewQuestions: builder.mutation<any, any>({
      query: (id) => ({
        url: `${API_URL.recruitmentJobs}/${id}/trigger-ai-questions`,
        method: "POST",
      }),
      invalidatesTags: invalidateOnSuccess([AI_GENERATED_INTERVIEW_QUESTIONS]),
    }),

    // PATCH: Update Interview Questions
    updateInterviewQuestions: builder.mutation<any, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `${API_URL.recruitmentJobs}/${id}/interview-questions`,
        method: "PUT",
        body,
      }),
    }),
    // ------ TalentHunt Endpoints ------

    // GET: All TalentHunt Job Posts
    getAllTalentHuntJobPosts: builder.query<
      AllTalentHuntJobPostsResponse,
      GetQueryArgsType<TalentHuntJobPostParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.talentHuntJob}`,
        method: "GET",
        ...queryArgs,
      }),
      providesTags: [TALENT_HUNT_JOB_POSTS],
    }),

    // GET: Application TalentHunt Job
    getApplicationTalentHuntJob: builder.query<
      ApplicationTalentHuntJobResponse,
      ApplicationTalentHuntJobParams
    >({
      query: ({ id, ...rest }) => ({
        url: `${API_URL.talentHuntJob}/${id}/applicants`,
        method: "GET",
        params: rest,
      }),
      providesTags: [APPLICANT_TALENT_HUNT_JOB],
    }),

    // POST: Trigger AI processing
    triggerAiProcessingTalentHuntJob: builder.mutation<void, string>({
      query: (id) => ({
        url: `${API_URL.talentHuntJob}/${id}/process`,
        method: "POST",
      }),
      invalidatesTags: (result) =>
        result
          ? [
              TALENT_HUNT_JOB_POSTS,
              APPLICANT_TALENT_HUNT_JOB,
              SYNC_TALENT_HUNT_JOB,
            ]
          : [],
    }),

    // GET: Sync & check AI search TalentHunt Job
    syncCheckAiSearchTalentHuntJob: builder.query<
      SyncCheckAiSearchTalentHuntJobResponse,
      string
    >({
      query: (id) => ({
        url: `${API_URL.talentHuntJob}/${id}/sync-status`,
        method: "GET",
      }),
      providesTags: [SYNC_TALENT_HUNT_JOB],
    }),

    // POST: Send emails to candidates
    talentHuntSendEmails: builder.mutation({
      query: (body) => ({
        url: API_URL.talentHuntSendEmails,
        method: "POST",
        body,
      }),
      invalidatesTags: invalidateOnSuccess([APPLICANT_TALENT_HUNT_JOB]),
    }),
  }),
});

export const {
  useGetAllJobPostsQuery,
  useGetSingleJobPostQuery,
  useCreateJobPostMutation,
  useUpdateJobPostMutation,
  useDeleteJobPostMutation,
  useSyncJobStatusMutation,
  useGetAllApplicantsQuery,
  useGetSingleApplicantsQuery,
  useDeleteApplicantsMutation,
  useShortlistCVMutation,
  useShortlistApplicantsManualMutation,
  useGetShortListedApplicantsQuery,
  useAnalyzeApplicantCvMutation,
  useGetApplicantAnalysisQuery,
  useAssignAssessmentMutation,
  useGetAllAssignedAssessmentQuery,
  useGetAllTalentHuntJobPostsQuery,
  useGetApplicationTalentHuntJobQuery,
  useTriggerAiProcessingTalentHuntJobMutation,
  useSyncCheckAiSearchTalentHuntJobQuery,
  useTalentHuntSendEmailsMutation,
  useGetAIGeneratedInterviewQuestionsQuery,
  useGenerateInterviewQuestionsMutation,
  useUpdateInterviewQuestionsMutation,
} = jobApplicantsApi;
